/**
 * MinimizedPopupContext
 *
 * A reusable context for managing minimized AllInOnePopup state across pages.
 * Supports up to 4 minimized popups, one per patient.
 * Allows minimizing the popup to small tabs at the bottom-left corner.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const MAX_MINIMIZED_POPUPS = 4;

const MinimizedPopupContext = createContext(null);

export const MinimizedPopupProvider = ({ children }) => {
  // Store array of minimized popup data: [{ treatment, selection, minimizedAt }]
  const [minimizedPopups, setMinimizedPopups] = useState([]);

  // Minimize the popup with treatment data (max 4, one per patient)
  const minimizePopup = useCallback((treatment, selection = null) => {
    const patientId = treatment?.patientId || treatment?.PatientId;

    setMinimizedPopups(prev => {
      // Check if this patient already has a minimized popup
      const existingIndex = prev.findIndex(p => {
        const pId = p.treatment?.patientId || p.treatment?.PatientId;
        return pId === patientId;
      });

      // If patient already has a minimized popup, update it
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          treatment,
          selection,
          minimizedAt: Date.now(),
        };
        return updated;
      }

      // If at max capacity, remove the oldest one
      if (prev.length >= MAX_MINIMIZED_POPUPS) {
        const sorted = [...prev].sort((a, b) => a.minimizedAt - b.minimizedAt);
        sorted.shift(); // Remove oldest
        return [...sorted, { treatment, selection, minimizedAt: Date.now() }];
      }

      // Add new minimized popup
      return [...prev, { treatment, selection, minimizedAt: Date.now() }];
    });
  }, []);

  // Restore/expand a popup by index
  const restorePopup = useCallback((index) => {
    const popup = minimizedPopups[index];
    if (popup) {
      setMinimizedPopups(prev => prev.filter((_, i) => i !== index));
      return popup;
    }
    return null;
  }, [minimizedPopups]);

  // Close a minimized popup by index without restoring
  const closeMinimizedPopup = useCallback((index) => {
    setMinimizedPopups(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Clear all minimized popups
  const clearAllMinimizedPopups = useCallback(() => {
    setMinimizedPopups([]);
  }, []);

  // Check if there are any minimized popups
  const hasMinimizedPopups = minimizedPopups.length > 0;

  // Get count of minimized popups
  const minimizedCount = minimizedPopups.length;

  return (
    <MinimizedPopupContext.Provider
      value={{
        minimizedPopups,
        minimizePopup,
        restorePopup,
        closeMinimizedPopup,
        clearAllMinimizedPopups,
        hasMinimizedPopups,
        minimizedCount,
        maxMinimizedPopups: MAX_MINIMIZED_POPUPS,
      }}
    >
      {children}
    </MinimizedPopupContext.Provider>
  );
};

export const useMinimizedPopup = () => {
  const context = useContext(MinimizedPopupContext);
  if (!context) {
    throw new Error('useMinimizedPopup must be used within a MinimizedPopupProvider');
  }
  return context;
};

export default MinimizedPopupContext;
